import {
  collection,
  addDoc,
  serverTimestamp,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  doc,
  updateDoc,
  getDocs,
} from 'firebase/firestore';
import { db, appId } from '@/config/firebase';

/**
 * Notification types
 */
export type NotificationType =
  | 'reply'
  | 'reaction'
  | 'mention'
  | 'dm'
  | 'outreach_new_member'
  | 'outreach_contacted'
  | 'outreach_summary';

export interface Notification {
  id: string;
  type: NotificationType;
  userId: string; // The user who should receive this notification
  fromUserId: string; // The user who triggered the notification
  fromUserName: string; // Display name of the user who triggered it
  messageId: string; // The message that was replied to
  replyMessageId?: string; // The reply message (for reply notifications)
  conversationId?: string; // For DM notifications
  preview: string; // Preview text
  timestamp: unknown; // serverTimestamp
  isRead: boolean;
}

/**
 * Get the path to a user's notifications collection
 */
function getNotificationsPath(userId: string): string {
  return `artifacts/${appId}/users/${userId}/notifications`;
}

/**
 * Create a reply notification for a user
 */
export async function createReplyNotification(
  recipientId: string,
  replierId: string,
  replierName: string,
  originalMessageId: string,
  replyMessageId: string,
  replyPreview: string
): Promise<void> {
  // Don't notify if user replied to their own message
  if (recipientId === replierId) {
    return;
  }

  const notificationsRef = collection(db, getNotificationsPath(recipientId));

  await addDoc(notificationsRef, {
    type: 'reply' as NotificationType,
    userId: recipientId,
    fromUserId: replierId,
    fromUserName: replierName,
    messageId: originalMessageId,
    replyMessageId,
    preview: replyPreview.length > 50 ? replyPreview.slice(0, 50) + '...' : replyPreview,
    timestamp: serverTimestamp(),
    isRead: false,
  });
}

/**
 * Subscribe to unread notifications for a user.
 *
 * NOTE: This query requires a composite index on (isRead, timestamp).
 * If you see an index error, click the link in the console to create it.
 *
 * Index: notifications collection
 * - isRead (Ascending)
 * - timestamp (Descending)
 */
export function subscribeToUnreadNotifications(
  userId: string,
  callback: (notifications: Notification[]) => void
): () => void {
  const notificationsRef = collection(db, getNotificationsPath(userId));

  // Use a simpler query that doesn't require a composite index
  // Filter for unread client-side to avoid index requirement
  const q = query(
    notificationsRef,
    orderBy('timestamp', 'desc'),
    limit(50) // Get more to account for read ones we'll filter out
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const allNotifications = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      })) as Notification[];

      // Filter to unread only (client-side)
      const unreadNotifications = allNotifications.filter((n) => !n.isRead).slice(0, 20);

      callback(unreadNotifications);
    },
    (error) => {
      // Handle index errors gracefully
      console.error('Notification subscription error:', error);
      callback([]);
    }
  );
}

/**
 * Mark a notification as read
 */
export async function markNotificationRead(userId: string, notificationId: string): Promise<void> {
  const notificationRef = doc(db, getNotificationsPath(userId), notificationId);
  await updateDoc(notificationRef, { isRead: true });
}

/**
 * Mark all notifications as read
 */
export async function markAllNotificationsRead(userId: string): Promise<void> {
  const notificationsRef = collection(db, getNotificationsPath(userId));
  const q = query(notificationsRef, where('isRead', '==', false));

  const snapshot = await getDocs(q);
  const updates = snapshot.docs.map((docSnapshot) =>
    updateDoc(doc(db, getNotificationsPath(userId), docSnapshot.id), { isRead: true })
  );

  await Promise.all(updates);
}

/**
 * Get unread notification count
 */
export function subscribeToUnreadCount(
  userId: string,
  callback: (count: number) => void
): () => void {
  const notificationsRef = collection(db, getNotificationsPath(userId));
  const q = query(notificationsRef, where('isRead', '==', false));

  return onSnapshot(q, (snapshot) => {
    callback(snapshot.size);
  });
}
